import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { familyApi, type FamilyNode, type FamilyView, type Relation, type RelationStep, type RelationWay } from '../features/family/api.ts';

// Island family tree. Links are worked out live by the server from the parent
// fields on each resident's record, so this page never edits anything — it is
// a window onto the register. `?id=` centres the tree on someone; `&with=` adds
// "how are these two related?".

type Search = { id?: string; with?: string };

function meta(n: FamilyNode): string {
  return [n.birthYear ? `b. ${n.birthYear}` : null, n.house?.name].filter(Boolean).join(' · ');
}

function stepWord(step: RelationStep): string {
  const s = step.node.sex;
  if (step.via === 'parent') return s === 'M' ? 'father' : s === 'F' ? 'mother' : 'parent';
  if (step.via === 'child') return s === 'M' ? 'son' : s === 'F' ? 'daughter' : 'child';
  if (step.via === 'partner') return s === 'M' ? 'husband' : s === 'F' ? 'wife' : 'partner';
  return '';
}

// ── Person card ──────────────────────────────────────────────────────────────

function PersonCard({ node, focus = false, note }: { node: FamilyNode | null; focus?: boolean; note?: string }): JSX.Element {
  if (!node) {
    return (
      <div className="rounded-2xl border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground">
        Not recorded
      </div>
    );
  }
  const deceased = node.status === 'DECEASED';
  const body = (
    <>
      <span className="flex items-center gap-1.5">
        <span className={`truncate font-medium leading-snug ${deceased ? 'text-muted-foreground' : 'text-ink'}`}>{node.name}</span>
      </span>
      <span className="mono-meta block truncate text-muted-foreground">
        {node.resident ? meta(node) || '—' : 'Not in register'}
      </span>
      {(note || deceased || node.status === 'MOVED_OUT') && (
        <span className="status-label mt-0.5 block text-primary">
          {[note, deceased ? 'Deceased' : node.status === 'MOVED_OUT' ? 'Moved away' : null].filter(Boolean).join(' · ')}
        </span>
      )}
    </>
  );
  const base = 'block min-w-0 rounded-2xl px-3 py-2.5 text-start transition-colors';
  if (!node.resident) {
    return <div className={`${base} border border-dashed border-border`}>{body}</div>;
  }
  return (
    <Link
      to="/family"
      search={{ id: node.id }}
      className={`${base} border ${focus ? 'border-primary ring-1 ring-primary bg-card shadow-sm' : 'border-border bg-card hover:bg-[hsl(var(--surface-high))]'}`}
    >
      {body}
    </Link>
  );
}

function Generation({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-2">
      <h2 className="status-label text-primary">{label}</h2>
      {children}
    </section>
  );
}

function Connector(): JSX.Element {
  return <div aria-hidden className="mx-auto h-5 w-px bg-border" />;
}

// ── Person picker (search residents) ─────────────────────────────────────────

function PersonPicker({ placeholder, onPick, autoFocus }: { placeholder: string; onPick: (n: FamilyNode) => void; autoFocus?: boolean }): JSX.Element {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const { data = [], isFetching } = useQuery({
    queryKey: ['family-search', debounced],
    queryFn: () => familyApi.search(debounced),
    enabled: debounced.length > 0,
  });
  return (
    <div ref={box} className="relative">
      <Icon name="search" size={18} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input
        className="input ps-10"
        value={q}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
      />
      {open && debounced && (
        <ul className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-y-auto rounded-2xl border border-border bg-card py-1 shadow-lg">
          {data.length === 0 ? (
            <li className="px-4 py-2.5 text-sm text-muted-foreground">{isFetching ? 'Searching…' : 'No one found'}</li>
          ) : data.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                className="block w-full px-4 py-2 text-start hover:bg-[hsl(var(--surface-high))]"
                onClick={() => { onPick(n); setQ(''); setOpen(false); }}
              >
                <span className="block truncate font-medium">{n.name}</span>
                <span className="mono-meta block truncate text-muted-foreground">{meta(n)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Relationship ─────────────────────────────────────────────────────────────

const SHOWN_WAYS = 3;

function Chain({ path }: { path: RelationStep[] }): JSX.Element {
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-2">
      {path.map((step, i) => (
        <li key={`${step.node.id}-${i}`} className="flex items-center gap-1">
          {i > 0 && (
            <span className="flex items-center gap-1 px-1 text-xs text-muted-foreground">
              <Icon name="chevron_right" size={16} />
              {stepWord(step)}
            </span>
          )}
          <span className="max-w-[13rem]"><PersonCard node={step.node} /></span>
        </li>
      ))}
    </ol>
  );
}

function WayItem({ way, index, a, b }: { way: RelationWay; index: number; a: FamilyNode; b: FamilyNode }): JSX.Element {
  const steps = way.path.length - 1;
  return (
    <li className="space-y-3 rounded-3xl border border-border p-4">
      <div className="space-y-0.5">
        <p className="status-label text-primary">
          {index === 0 ? 'Closest' : `Way ${index + 1}`} · {steps} step{steps === 1 ? '' : 's'}
        </p>
        <p className="font-serif text-lg tracking-tight">{way.label}</p>
        {way.kind !== 'distant' && (
          <p className="text-sm text-muted-foreground">
            <span className="text-ink">{b.name}</span> is <span className="text-ink">{a.name}</span>'s {way.label.charAt(0).toLowerCase() + way.label.slice(1)}
            {way.commonAncestors.length > 0 && <> · through {way.commonAncestors.map((n) => n.name).join(' & ')}</>}
          </p>
        )}
      </div>
      <Chain path={way.path} />
    </li>
  );
}

function RelationCard({ relation, a, b }: { relation: Relation; a: FamilyNode; b: FamilyNode }): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  if (relation.kind === 'none' || !relation.label) {
    return (
      <div className="card-paper space-y-1 p-5">
        <p className="font-serif text-xl">No family connection found</p>
        <p className="text-sm text-muted-foreground">
          The register doesn't link {b.name} to {a.name} through parents or marriages. A missing or misspelt parent name is the usual reason.
        </p>
      </div>
    );
  }
  const ways = relation.ways;
  const shown = showAll ? ways : ways.slice(0, SHOWN_WAYS);
  return (
    <div className="card-paper space-y-4 p-5">
      <div className="space-y-1">
        <p className="status-label text-primary">How they are related</p>
        <p className="font-serif text-2xl tracking-tight">{relation.label}</p>
        <p className="text-sm text-muted-foreground">
          {ways.length === 1
            ? 'Connected one way.'
            : `Connected ${ways.length} ways${ways.length >= 12 ? ' or more' : ''} · shortest first.`}
        </p>
      </div>
      <ol className="space-y-3">
        {shown.map((way, i) => <WayItem key={way.path.map((s) => s.node.id).join('>')} way={way} index={i} a={a} b={b} />)}
      </ol>
      {ways.length > SHOWN_WAYS && (
        <button type="button" className="btn-ghost" onClick={() => setShowAll((v) => !v)}>
          <Icon name={showAll ? 'expand_less' : 'expand_more'} size={16} />
          {showAll ? 'Show fewer' : `Show all ${ways.length} ways`}
        </button>
      )}
    </div>
  );
}

// ── Family view ──────────────────────────────────────────────────────────────

function Couple({ label, pair }: { label: string; pair: [FamilyNode | null, FamilyNode | null] }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="grid grid-cols-2 gap-2">
        <PersonCard node={pair[0]} />
        <PersonCard node={pair[1]} />
      </div>
    </div>
  );
}

function TreeView({ view }: { view: FamilyView }): JSX.Element {
  const { focus, father, mother, grandparents, siblings, families } = view;
  const kids = families.reduce((n, f) => n + f.children.length, 0);
  return (
    <div className="space-y-2">
      <Generation label="Grandparents">
        <div className="grid gap-3 sm:grid-cols-2">
          <Couple label="Father's side" pair={grandparents.paternal} />
          <Couple label="Mother's side" pair={grandparents.maternal} />
        </div>
      </Generation>
      <Connector />
      <Generation label="Parents">
        <div className="mx-auto grid max-w-xl grid-cols-2 gap-2">
          <PersonCard node={father} note="Father" />
          <PersonCard node={mother} note="Mother" />
        </div>
      </Generation>
      <Connector />
      <div className="mx-auto max-w-sm">
        <PersonCard node={focus} focus />
      </div>
      {siblings.length > 0 && (
        <Generation label={`Brothers & sisters · ${siblings.length}`}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {siblings.map((s) => (
              <PersonCard key={s.node.id} node={s.node} note={s.half ? 'Half-sibling' : undefined} />
            ))}
          </div>
        </Generation>
      )}
      {kids > 0 && (
        <>
          <Connector />
          <Generation label={`Children · ${kids}`}>
            <div className="space-y-4">
              {families.map((fam, i) => (
                <div key={fam.partner?.id ?? `none-${i}`} className="space-y-2 rounded-3xl border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {fam.partner ? `With ${focus.sex === 'F' ? 'husband' : focus.sex === 'M' ? 'wife' : 'partner'}` : 'Other parent not recorded'}
                    </span>
                    {fam.partner && <span className="max-w-xs"><PersonCard node={fam.partner} /></span>}
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {fam.children.map((c) => (
                      <div key={c.node.id} className="space-y-1.5">
                        <PersonCard node={c.node} />
                        {c.children.length > 0 && (
                          <p className="ps-3 text-xs text-muted-foreground">
                            {c.children.length} grandchild{c.children.length > 1 ? 'ren' : ''}:{' '}
                            {c.children.map((g, gi) => (
                              <span key={g.id}>
                                {gi > 0 && ', '}
                                {g.resident ? (
                                  <Link to="/family" search={{ id: g.id }} className="text-primary hover:underline">{g.name}</Link>
                                ) : g.name}
                              </span>
                            ))}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Generation>
        </>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function Landing(): JSX.Element {
  const navigate = useNavigate();
  const [first, setFirst] = useState<FamilyNode | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['family-groups'], queryFn: familyApi.groups });
  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <PersonPicker autoFocus placeholder="Find a resident by name or house…" onPick={(n) => void navigate({ to: '/family', search: { id: n.id } })} />
      </section>

      <section className="card-paper space-y-3 p-5">
        <div>
          <h2 className="font-serif text-lg font-bold">How are two people related?</h2>
          <p className="text-sm text-muted-foreground">Pick two residents to see the family chain between them.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {first ? (
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1"><PersonCard node={first} /></span>
              <button type="button" className="btn-ghost" onClick={() => setFirst(null)} aria-label="Change first person">
                <Icon name="close" size={18} />
              </button>
            </div>
          ) : (
            <PersonPicker placeholder="First person…" onPick={setFirst} />
          )}
          {first && (
            <PersonPicker placeholder="Second person…" onPick={(n) => void navigate({ to: '/family', search: { id: first.id, with: n.id } })} />
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-serif text-lg font-bold">Families on the island</h2>
          {data && (
            <p className="text-sm text-muted-foreground">
              {data.residents} residents · {data.groups.length} connected famil{data.groups.length === 1 ? 'y' : 'ies'}
              {data.unlinked > 0 && ` · ${data.unlinked} not linked to anyone else yet`}
            </p>
          )}
        </div>
        {isLoading ? (
          <p className="status-label" role="status">Loading…</p>
        ) : (
          <ul className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
            {(data?.groups ?? []).map((g) => (
              <li key={g.head.id} className="border-b border-black/[0.05] last:border-b-0 dark:border-white/[0.06]">
                <Link
                  to="/family"
                  search={{ id: g.head.id }}
                  className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-ink">{g.head.name}</div>
                    <div className="mono-meta truncate text-muted-foreground">
                      Eldest · {meta(g.head)}
                    </div>
                  </div>
                  <span className="mono-meta shrink-0 text-end text-muted-foreground">
                    <span className="block text-base font-semibold text-ink">{g.residents}</span>
                    {g.houses} house{g.houses === 1 ? '' : 's'}
                  </span>
                  <Icon name="chevron_right" size={18} className="shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PersonTree({ id, withId }: { id: string; withId?: string }): JSX.Element {
  const navigate = useNavigate();
  const { data: view, isLoading, isError } = useQuery({ queryKey: ['family-view', id], queryFn: () => familyApi.view(id) });
  const { data: relation } = useQuery({
    queryKey: ['family-relation', id, withId],
    queryFn: () => familyApi.relation(id, withId!),
    enabled: !!withId,
  });
  const { data: other } = useQuery({
    queryKey: ['family-view', withId],
    queryFn: () => familyApi.view(withId!),
    enabled: !!withId,
  });

  if (isLoading) return <p className="status-label" role="status">Loading…</p>;
  if (isError || !view) return <p className="card-paper p-6 text-center text-sm text-muted-foreground">This person couldn't be found.</p>;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl tracking-tight">{view.focus.name}</h1>
          <p className="mono-meta mt-1 text-muted-foreground">{meta(view.focus)}</p>
        </div>
        <Link to="/people/$personId" params={{ personId: view.focus.id }} className="btn-ghost">
          <Icon name="person" size={16} /> Open record
        </Link>
      </header>

      <div className="max-w-xl space-y-1.5">
        <p className="status-label">Compare with someone</p>
        <PersonPicker placeholder="How is someone related to this person?" onPick={(n) => void navigate({ to: '/family', search: { id, with: n.id } })} />
      </div>

      {withId && relation && other && (
        <div className="space-y-2">
          <RelationCard key={withId} relation={relation} a={view.focus} b={other.focus} />
          <Link to="/family" search={{ id }} className="btn-ghost">
            <Icon name="close" size={16} /> Clear comparison
          </Link>
        </div>
      )}

      <TreeView view={view} />
    </div>
  );
}

export function FamilyTree(): JSX.Element {
  const { id, with: withId } = useSearch({ strict: false }) as Search;
  return (
    <div className="page space-y-6">
      {id ? (
        <Link to="/family" className="btn-ghost"><Icon name="arrow_back" size={15} /> Family tree</Link>
      ) : (
        <header className="space-y-1">
          <h1 className="font-serif text-3xl tracking-tight">Family tree</h1>
          <p className="text-sm text-muted-foreground">
            Worked out from each resident's father, mother and grandfather in the register — it updates by itself whenever records change.
          </p>
        </header>
      )}
      {id ? <PersonTree key={id} id={id} withId={withId} /> : <Landing />}
    </div>
  );
}
